import { useEffect, useRef, useState } from 'react';
import { anneesDisponibles, CLE_CLOTURES, premiereAnneeDuComptable } from '../../lib/cloture';
import { TAUX_LEGAUX_ACTUELS } from '../../lib/tva';
import { tvaApi } from '../../api/resources';
import { FRAIS_CAT } from '../../constants/accounting';
import {
  auCentime,
  compteDansLeCa,
  decompteTva,
  htDocument,
  htVente,
  signeCa,
  ttcDocument,
} from '../../lib/vat';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { SuiviAffaires } from './SuiviAffaires.jsx';
import { MonAnnee } from './MonAnnee.jsx';
import { dejaPayeEtat, paiementEtat, sansPaiementsEtat } from '../../lib/paiements-etat';
import { factureDeLaPeriode } from '../../lib/facture-periode';
import { chantierCosts } from '../../lib/costs';
import { achatsDuChantier, useAchatsJoints } from '../../components/ListeDuClient.jsx';
import { usePaiements } from '../../components/PaiementsRecus.jsx';
import { useReglages } from '../../api/reglages.jsx';
import {
  CLE_REGLE_ACOMPTES,
  piecesComptables,
  reglesAcomptes,
} from '../../lib/acomptes-anterieurs';
import { recuDansLaPeriode } from '../../lib/paiements-ecrits';
import { useChargesAnnexes } from '../../lib/charges-annexes';
import { brutDuMois } from '../../lib/salaires-periode';
import { depensesHt } from '../../lib/depenses-ht';
import { useMaFiche } from '../../api/maFiche';
import { estIndependant } from '../equipe/EmpEditor.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { LANG, tr, trf } from '../../lib/i18n';
import { impotsAProvisionner, tauxCompta, tauxPatronauxTotal } from '../../lib/charges';
import { modulesSouscrits } from '../../lib/modules';
import { bornes, dansPeriode, detailParPeriode, litDate } from '../../lib/periodes';
import { marchandiseAuDepot, valeurDuStock, variationDuStock } from '../../lib/valeur-stock';
import { C } from '../../theme/palette';

/** Taux de TVA retenu quand le document n'en porte pas. */

const caseSalaire = {
  background: C.white,
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '9px 6px',
  textAlign: 'center',
};

// Le TTC se derivait du hors-taxe au seul taux de tete du document : toute
// facture melant plusieurs taux — du materiel a 2.6 % et de la pose a 8.1 %,
// par exemple — etait mal comptee dans le chiffre d'affaires, les encaissements
// et le classement des clients.
const ttcOf = (f) => ttcDocument(f);

/**
 * Tableau de bord financier de l'accueil.
 *
 * Il n'est montré qu'aux rôles qui ont droit aux chiffres : l'appelant s'en
 * assure, ce composant ne refait pas le contrôle. Les chiffres se recalculent
 * à chaque rendu plutôt que d'être conservés : ils dérivent tous de
 * collections déjà en mémoire, et une valeur figée serait une valeur fausse
 * dès la facture suivante.
 */
/** Circonférence de l'anneau de santé (rayon 24), arrondie comme la maquette. */
/** Une date en AAAA-MM-JJ, jour local. */
const isoJour = (d) =>
  d.getFullYear() +
  '-' +
  String(d.getMonth() + 1).padStart(2, '0') +
  '-' +
  String(d.getDate()).padStart(2, '0');

/** Largeur des colonnes chiffrées du détail, pour qu'elles s'alignent. */
const COLONNE = { width: 74, textAlign: 'right' };
const CHIFFRE = { ...COLONNE, fontSize: 10.5, fontWeight: 800 };
// 9'340 → « 9.3k », 12'500 → « 13k » : tient au-dessus d'une barre étroite.
const enMilliers = (v) =>
  v >= 1000 ? (v / 1000).toFixed(v >= 10000 ? 0 : 1) + 'k' : String(Math.round(v));

export function TableauBord({
  onNav,
  factures,
  devis,
  chantiers,
  employes,
  clients,
  frais,
  stock,
  isMag,
  ventes,
  tauxVentes,
  aTraiter = null,
  carteClients = null,
  maintenant = new Date(),
  soc,
  estMixte = false,
}) {
  // L'année d'avant, l'année en cours, le mois, le trimestre ou tout : le
  // patron compare 2025 et 2026 d'un toucher.
  const [choix, setChoix] = useState({ periode: 'annee', decalage: 0 });
  const { periode, decalage } = choix;
  // Un acompte versé l'année d'avant sa facture compte dans l'année choisie
  // par la société (Compta → Synthèse), comme dans la comptabilité.
  const { reglageJson } = useReglages();
  const { paiements } = usePaiements((factures || []).length);
  const facturesCompta = piecesComptables(
    factures,
    paiements,
    reglesAcomptes(reglageJson(CLE_REGLE_ACOMPTES, null)),
  );
  const maFiche = useMaFiche();

  const [debut, fin] = bornes(periode, decalage, maintenant);
  // Les taux (charges, AVS, impôts) de l'année montrée : l'onglet 2025
  // prenait ceux de l'année en cours, et son bénéfice différait de la Compta.
  const anneeTaux =
    periode === 'tout' || !(fin instanceof Date) ? maintenant.getFullYear() : fin.getFullYear();

  // Un avoir n'est pas une recette : la comptabilite l'ecarte, le detail par
  // periode aussi. La tuile l'additionnait, et deux chiffres d'affaires se
  // contredisaient sur le meme ecran.
  // Une vente au comptoir est une recette au meme titre qu'une facture : le
  // tableau de bord d'un magasin l'ignorait.
  const tauxVente = (v) => (v.tx != null ? +v.tx : (tauxVentes ?? TAUX_LEGAUX_ACTUELS.normal));
  const ventesEntre = (a, b, avecTva) =>
    (ventes || []).reduce(
      (t, v) =>
        t +
        (dansPeriode(v.date, a, b, maintenant)
          ? avecTva
            ? (htVente(v) * tauxVente(v)) / 100
            : htVente(v) * (1 + tauxVente(v) / 100)
          : 0),
      0,
    );
  // Hors taxe, comme la comptabilité : la TVA encaissée n'est pas à
  // l'entreprise, elle la reverse. Le chiffre TTC, moins des frais TTC,
  // annonçait un bénéfice qui comptait la TVA due.
  // L'année que la société a choisie pour un acompte (« année des
  // travaux ») range la pièce, comme dans la Compta : l'accueil lisait la
  // date de la facture, et les deux chiffres d'affaires différaient.
  const dateCa = (f) => f.dateCompta || f.date;
  const caEntre = (a, b) =>
    facturesCompta.reduce(
      (t, f) =>
        t +
        (compteDansLeCa(f) && dansPeriode(dateCa(f), a, b, maintenant)
          ? signeCa(f) * htDocument(f)
          : 0),
      0,
    ) + ventesHtEntre(a, b);
  const ventesHtEntre = (a, b) =>
    (ventes || []).reduce(
      (t, v) => t + (dansPeriode(v.date, a, b, maintenant) ? htVente(v) : 0),
      0,
    );
  // La marchandise et la sous-traitance jointes aux offres et factures, même
  // payées, sont des dépenses comme les autres.
  const achatsJoints = useAchatsJoints((factures || []).length + ':' + (devis || []).length);
  // Toutes les dépenses : Frais, factures des fournisseurs des chantiers,
  // salaires et charges. L'accueil ne lisait que Frais et affichait
  // « Dépenses 0.00 · Marge 100 % » sur des chantiers qui avaient coûté.
  // Les charges patronales aux taux de la société (Réglages → Compta), comme
  // la comptabilité : un taux fixe de 15 % donnait à l'accueil un autre
  // bénéfice que celui de la Compta. La CCT du bâtiment est due par une
  // société Mix, quel que soit l'univers affiché.
  const chargesSociales =
    tauxPatronauxTotal(
      tauxCompta(soc, anneeTaux),
      modulesSouscrits(estMixte ? 'mixte' : soc?.secteur).batiment,
    ) / 100;
  // Les véhicules (leasing, amortissement) et le comptable sont des charges
  // de l'année, comme dans la Compta : sans eux, le bénéfice de l'accueil
  // dépassait celui de la comptabilité.
  const { vehicules, comptableAnnuel, comptable } = useChargesAnnexes();
  // Chaque achat joint dit d'où il vient : « offre 12 », « facture 07 ».
  const achatsNommes = achatsJoints.map((x) => {
    const facture = x?.genre === 'FACTURE';
    const doc = ((facture ? factures : devis) || []).find((d) => d.id === x?.documentId);
    return doc ? { ...x, document: (facture ? tr('facture') : tr('offre')) + ' ' + doc.num } : x;
  });
  const avecSalaries = (employes || []).some(
    (e) => !(e?.patronIndependant || (estIndependant(soc?.forme) && maFiche.estLaMienne(e))),
  );
  const toutesDepenses = depensesHt({
    frais,
    chantiers,
    achatsJoints: achatsNommes,
    factures,
    devis,
    employes,
    soc,
    chargesSociales,
    exclu: (e) => e?.patronIndependant || (estIndependant(soc?.forme) && maFiche.estLaMienne(e)),
    maintenant,
    vehicules,
    comptableAnnuel,
    comptable,
    comptableDepuis: premiereAnneeDuComptable(frais, maintenant),
    salairesEstimes: avecSalaries,
    lppDansLesSalaires: avecSalaries && +tauxCompta(soc, anneeTaux).lpp > 0,
    // « Tout » : les salaires, véhicules et comptable de toutes les années
    // qui ont des pièces, pas des trois dernières seulement.
    // Seulement les années qui ont une facture ou une dépense : les
    // honoraires du comptable et les véhicules comptaient aussi pour 2024,
    // une année sans aucune pièce (« 2024 · bénéfice −2'000.00 »).
    annees: Math.max(
      1,
      maintenant.getFullYear() - Math.min(...anneesDisponibles(factures, frais, maintenant)) + 1,
    ),
  });
  const depensesEntre = (a, b, cle = 'montant') =>
    toutesDepenses.reduce((t, d) => t + (dansPeriode(d.date, a, b, maintenant) ? d[cle] : 0), 0);
  // L'encaissement se date du paiement, pas de l'émission : une facture de
  // décembre payée en janvier appartient à la trésorerie de janvier.
  // Chaque paiement à sa date (« Paiements reçus ») ; sinon la part perçue à
  // la date de paiement. Une facture payée en partie et les ventes au
  // comptoir, payées sur place, manquaient à la trésorerie.
  const encaisseEntre = (a, b) =>
    (factures || []).reduce((t, f) => {
      if (f.type === 'avoir' || !compteDansLeCa(f)) return t;
      const percu =
        f.statut === 'Payée'
          ? ttcOf(f)
          : f.statut === 'Partielle'
            ? Math.min(+f.paidAmount || 0, ttcOf(f))
            : 0;
      // Chaque paiement à sa date, l'acompte écrit en bas de la facture
      // compris : celui de 2025 se recomptait dans la trésorerie de 2026.
      return (
        t +
        recuDansLaPeriode(f, paiements?.get(f.id), percu, (d) => dansPeriode(d, a, b, maintenant))
      );
    }, 0) + ventesEntre(a, b, false);

  const ca = caEntre(debut, fin);
  const depenses = depensesEntre(debut, fin);
  // Au magasin, la marchandise achetée cette année et restée au dépôt n'est
  // pas une charge : la hausse du stock s'ajoute au bénéfice de l'année,
  // comme dans la Compta (stock au 1er janvier retrouvé depuis le journal du dépôt).
  const resteAuDepot =
    isMag && periode === 'annee'
      ? (variationDuStock({
          stock,
          clotures: reglageJson(CLE_CLOTURES, {}),
          annee: anneeTaux,
          maintenant,
        }) ?? 0)
      : 0;
  const benefice = ca - depenses + resteAuDepot;
  // L'argent versé à l'État (TVA, AVS, impôts) a quitté le compte le jour
  // du paiement : ce n'est pas une charge, mais il n'est plus là. Il restait
  // compté sur le compte (10'810.– affichés, 7'000.– en banque).
  const verseALEtat = (frais || [])
    .filter((x) => paiementEtat(x?.type) && dansPeriode(x.payeLe || x.date, debut, fin, maintenant))
    .reduce((t, x) => t + (+x.montant || 0), 0);
  // Un trop-perçu rendu au client a quitté le compte le jour du remboursement.
  const rembourse = (factures || []).reduce(
    (t, f) =>
      t +
      (f.remboursement?.montant > 0 &&
      dansPeriode(f.remboursement.date || f.paidDate || f.date, debut, fin, maintenant)
        ? +f.remboursement.montant
        : 0),
    0,
  );
  const tresorerie =
    encaisseEntre(debut, fin) - depensesEntre(debut, fin, 'ttc') - verseALEtat - rembourse;

  // La provision s'extrayait du chiffre d'affaires a un taux unique ; c'est la
  // TVA reellement portee par les factures de la periode qu'il faut mettre de
  // cote.
  // C'est le solde qu'il faut mettre de côté : la TVA facturée moins celle
  // payée sur les achats, au centime. Le serveur le calcule sur toutes les
  // pièces (avoirs, factures fournisseurs des chantiers, méthode réglée) ;
  // l'estimation locale ne sert qu'en attendant sa réponse ou sans lui.
  // L'estimation suit la règle du serveur (decompteTva) : la méthode réglée,
  // l'acompte facturé à sa date et la facture finale qui le déduit, les
  // paiements partiels pour leur part, les avoirs déduits, la dépense à sa
  // date comptable, au taux de sa date, et jamais un paiement à l'État. Elle
  // lisait les pièces comptables (l'acompte rangé dans l'année des travaux),
  // la TVA entière d'une facture à l'encaissement, et comptait la TVA notée
  // sur un paiement à l'AFC comme récupérable.
  const dansLaPeriode = (d) => dansPeriode(d, debut, fin, maintenant);
  const tvaEstimee = decompteTva({
    factures,
    paiements,
    dansLaPeriode,
    methode: soc?.tvaMethode === 'recues' ? 'recues' : 'convenues',
    ventes: (ventes || []).filter((v) => dansLaPeriode(v.date)),
    tauxVentes: tauxVentes ?? TAUX_LEGAUX_ACTUELS.normal,
    frais: sansPaiementsEtat(frais).filter((x) => dansLaPeriode(x.dateCompta || x.date)),
    categories: FRAIS_CAT,
    soc,
  }).nette;
  // La position TVA du serveur : facturée, récupérable sur tous les achats
  // (dépenses, fournisseurs des chantiers, achats joints, sous-traitants, TVA
  // lue sur chaque pièce) et le solde. L'accueil n'en gardait que le solde et
  // recalculait le récupérable : un centime d'écart avec la Compta.
  const [positionTva, setPositionTva] = useState(null);
  const tvaServeur = positionTva ? positionTva.solde : null;
  const debutIso = isoJour(debut);
  const finIso = isoJour(fin);
  // Ce qui change la TVA, pas seulement le nombre de pièces : une dépense
  // passée de « Matériel » à « Loyer », une date ou un montant corrigés
  // laissaient l'ancien chiffre affiché.
  const versionPieces = [
    ...(factures || []).map((f) =>
      [
        f.id,
        f.statut,
        f.date,
        f.dateCompta,
        f.paidAmount,
        f.paidDate,
        JSON.stringify(f.lignes),
      ].join(','),
    ),
    ...(frais || []).map((x) =>
      [x.id, x.type, x.date, x.dateCompta, x.montant, x.montantTva, x.tva].join(','),
    ),
    ...(ventes || []).map((v) => [v.id, v.date, v.total, v.tx].join(',')),
    achatsJoints.length,
  ].join('|');
  const periodeLue = useRef('');
  useEffect(() => {
    let actif = true;
    const periodeTva = debutIso + '|' + finIso;
    const nouvellePeriode = periodeLue.current !== periodeTva;
    periodeLue.current = periodeTva;
    // Une autre période : l'ancien chiffre ne vaut plus. Une pièce modifiée :
    // il reste affiché le temps que l'écriture arrive au serveur.
    if (nouvellePeriode) setPositionTva(null);
    const minuteur = setTimeout(
      () =>
        Promise.resolve()
          .then(() => tvaApi.position(debutIso, finIso))
          .then((p) => {
            if (actif && p && p.solde != null) {
              setPositionTva({
                solde: +p.solde,
                facturee: p.facturee?.total != null ? +p.facturee.total : null,
                recuperable: p.recuperable?.total != null ? +p.recuperable.total : null,
                estimees: +p.recuperable?.estimees || 0,
              });
            }
          })
          .catch(() => {}),
      nouvellePeriode ? 0 : 1500,
    );
    return () => {
      actif = false;
      clearTimeout(minuteur);
    };
  }, [debutIso, finIso, versionPieces]);
  const tvaProvision = tvaServeur ?? tvaEstimee;
  // Facturé TVA comprise sur la période, et la TVA qu'il porte : facturé
  // TTC moins le chiffre d'affaires hors TVA. Le reste de la provision est
  // la TVA payée sur les achats, que l'AFC rembourse.
  const factureTtc = auCentime(
    facturesCompta.reduce(
      (t, f) =>
        t +
        (compteDansLeCa(f) && dansPeriode(dateCa(f), debut, fin, maintenant)
          ? signeCa(f) * ttcDocument(f)
          : 0),
      0,
    ) + ventesEntre(debut, fin, false),
  );
  // Les honoraires du comptable de la période pas encore payés sont une
  // dépense de l'année, comme dans « sans TVA » : sans eux, les dépenses
  // « avec TVA » se lisaient plus petites que « sans TVA ». La trésorerie,
  // elle, ne compte que ce qui est payé.
  const comptableDe = (cle) =>
    toutesDepenses
      .filter((d) => d.genre === 'comptable' && dansPeriode(d.date, debut, fin, maintenant))
      .reduce((t, d) => t + (+d[cle] || 0), 0);
  const comptableAPayer = Math.max(0, comptableDe('montant') - comptableDe('ttc'));
  const depensesTtc = depensesEntre(debut, fin, 'ttc') + comptableAPayer;

  // Les impôts à mettre de côté sur le bénéfice de la période, selon la forme
  // de la société (Réglages) : AVS et impôt sur le revenu pour l'indépendant,
  // impôt sur le bénéfice et sur le capital pour une Sàrl ou une SA.
  const independant = estIndependant(soc?.forme);
  const TX = tauxCompta(soc, anneeTaux);
  const capitalSocial = +(soc?.capital ?? 20000);
  const premiereAnnee = (factures || []).reduce((min, f) => {
    const d = litDate(f.date, maintenant);
    return d && d.getFullYear() < min ? d.getFullYear() : min;
  }, maintenant.getFullYear());
  const partAnnee =
    periode === 'mois'
      ? 1 / 12
      : periode === 'trimestre'
        ? 1 / 4
        : periode === 'annee'
          ? 1
          : maintenant.getFullYear() - premiereAnnee + 1;
  const impots = impotsAProvisionner({
    benefice,
    independant,
    TX,
    capital: capitalSocial,
    partAnnee,
    annee: anneeTaux,
  });

  const rentabilite = (chantiers || [])
    .filter((c) => c.statut === 'En cours' || (c.facture || 0) > 0)
    .map((c) => {
      const facture = c.facture || 0;
      const cout = chantierCosts(
        c,
        employes,
        soc,
        factures,
        frais,
        achatsDuChantier(c, achatsJoints, devis, factures),
      );
      // Sans aucun coût noté, la marge se lisait 100 % : un chantier
      // terminé paraissait parfait alors qu'il manquait ses achats et ses
      // heures. Sans facture, 0 % ne voulait rien dire non plus.
      const couts = facture - cout.ben;
      return {
        nom: c.nom || c.client || 'Chantier',
        statut: c.statut,
        enCours: c.statut === 'En cours',
        facture,
        ben: cout.ben,
        sansCout: facture > 0 && Math.abs(couts) < 0.005,
        marge: facture > 0 ? Math.round((cout.ben / facture) * 100) : null,
      };
    })
    // Les chantiers en cours d'abord, puis les terminés : la carte disait
    // « en cours » et mêlait les deux dans le désordre.
    .sort((a, b) => Number(b.enCours) - Number(a.enCours))
    .slice(0, 4);

  // Un employé payé à l'heure n'a pas de salaire mensuel : ses heures du
  // mois en tiennent lieu. La carte l'ignorait, et disparaissait pour une
  // équipe payée à l'heure.
  const moisCourant =
    maintenant.getFullYear() + '-' + String(maintenant.getMonth() + 1).padStart(2, '0');
  // Au début du mois, ses heures du mois ne sont pas encore pointées : le
  // mois d'avant en donne l'estimation (le 1er, toute l'équipe à l'heure
  // comptait 0.–).
  const avant = new Date(maintenant.getFullYear(), maintenant.getMonth() - 1, 1);
  const moisAvant = avant.getFullYear() + '-' + String(avant.getMonth() + 1).padStart(2, '0');
  const salairesBruts = (employes || [])
    .filter((e) => !e.dateFin)
    .reduce(
      (a, e) =>
        a +
        (+e.salBase > 0
          ? +e.salBase
          : brutDuMois(e, chantiers, moisCourant, soc) || brutDuMois(e, chantiers, moisAvant, soc)),
      0,
    );
  const charges = Math.round(salairesBruts * chargesSociales);

  const valeurStock = valeurDuStock(stock);
  const sansPrixDeVente = (stock || []).filter((m) => +m.stock > 0 && !(+m.pv > 0)).length;
  const fmtQte = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

  // Hors taxes et au centime, comme la tuile « CA facturé (HT) » : en TTC
  // arrondi au franc, une facture de 10.70 se lisait 11.00.
  const topClients = (clients || [])
    .map((c) => {
      // Sur la période choisie (Mois, Trim., Année, Tout), comme les chiffres
      // du dessus : un client « depuis toujours » dépassait le CA de l'année.
      const siennes = facturesCompta.filter(
        (f) =>
          f.clientId === c.id &&
          compteDansLeCa(f) &&
          dansPeriode(dateCa(f), debut, fin, maintenant),
      );
      return {
        id: c.id,
        nom: c.nom,
        nb: siennes.length,
        ca: siennes.reduce((a, f) => a + signeCa(f) * htDocument(f), 0),
      };
    })
    .filter((c) => c.ca > 0)
    .sort((a, b) => b.ca - a.ca)
    .slice(0, 5);

  // Douze mois glissants : l'année civile couperait l'historique en janvier,
  // au moment précis où la comparaison est la plus utile.
  const MOIS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
  // L'initiale du mois dans la langue de l'entreprise (en français : J F M…).
  let moisCourt = null;
  try {
    moisCourt = new Intl.DateTimeFormat(LANG || 'fr', { month: 'narrow' });
  } catch {
    moisCourt = null;
  }
  // Une passe sur les factures, rangées par mois, au lieu de douze passes.
  const caParCle = new Map();
  for (const f of facturesCompta) {
    const d = litDate(dateCa(f), maintenant);
    if (!d) {
      continue;
    }
    const cle = d.getFullYear() + '-' + d.getMonth();
    caParCle.set(
      cle,
      (caParCle.get(cle) || 0) + (compteDansLeCa(f) ? signeCa(f) * ttcDocument(f) : 0),
    );
  }
  // Les ventes de la caisse aussi, avec TVA : la case « Facturé avec TVA »
  // les compte, le graphique les oubliait — un magasin n'y voyait rien.
  for (const v of ventes || []) {
    const d = litDate(v.date, maintenant);
    if (!d) continue;
    const cle = d.getFullYear() + '-' + d.getMonth();
    caParCle.set(cle, (caParCle.get(cle) || 0) + htVente(v) * (1 + tauxVente(v) / 100));
  }
  const caParMois = [];
  for (let k = 11; k >= 0; k--) {
    const dt = new Date(maintenant.getFullYear(), maintenant.getMonth() - k, 1);
    caParMois.push({
      lb: moisCourt ? moisCourt.format(dt) : MOIS[dt.getMonth()],
      v: caParCle.get(dt.getFullYear() + '-' + dt.getMonth()) || 0,
    });
  }

  // Les tuiles disent ou on en est ; ce detail dit comment on y est arrive.
  // Les mêmes pièces que la case CA : factures émises (brouillons exclus,
  // avoirs en moins) et ventes de la caisse. Le détail comptait les
  // brouillons et oubliait la caisse : un magasin y voyait un autre chiffre
  // que la case du dessus.
  const recettes = [
    ...facturesCompta.filter(compteDansLeCa).map((f) => ({
      date: dateCa(f),
      montant: signeCa(f) * htDocument(f),
      ttc: signeCa(f) * ttcDocument(f),
    })),
    ...(ventes || []).map((v) => ({
      date: v.date,
      montant: htVente(v),
      ttc: htVente(v) * (1 + tauxVente(v) / 100),
    })),
  ];
  const detail = detailParPeriode({
    periode: periode === 'tout' ? 'annee' : periode,
    factures: recettes,
    frais: toutesDepenses,
    ca: (r) => r.montant,
    ttc: (r) => r.ttc,
    maintenant,
  });
  // Les acomptes d'une année rangés dans une autre : la ligne de l'année le
  // dit (« dont acomptes : 7'807.00 + 3'070.00 »).
  const acomptesDeLAnnee = (annee) =>
    factureDeLaPeriode({
      factures,
      pieces: facturesCompta,
      dansLaPeriode: (d) => litDate(d, maintenant)?.getFullYear() === annee,
    }).entrants;

  const moisLong = (d) => {
    try {
      return new Intl.DateTimeFormat(LANG || 'fr', { month: 'long', year: 'numeric' }).format(d);
    } catch {
      return d.getMonth() + 1 + '.' + d.getFullYear();
    }
  };
  const libellePeriode =
    periode === 'annee'
      ? String(debut.getFullYear())
      : periode === 'mois'
        ? moisLong(debut)
        : periode === 'trimestre'
          ? 'T' + (Math.floor(debut.getMonth() / 3) + 1) + ' ' + debut.getFullYear()
          : tr('depuis le début');
  // L'année d'avant et l'année en cours, puis le mois, le trimestre, tout.
  const an = maintenant.getFullYear();
  const choixPeriodes = [
    ['annee', 1, String(an - 1)],
    ['annee', 0, String(an)],
    ['mois', 0, tr('Mois')],
    ['trimestre', 0, tr('Trim.')],
    ['tout', 0, tr('Tout')],
  ];

  const bouton = (on) => ({
    flex: 1,
    padding: '7px 4px',
    fontSize: 9.5,
    fontWeight: 800,
    borderRadius: 9,
    border: '1px solid ' + (on ? C.navy : C.border),
    background: on ? C.navy : C.white,
    color: on ? '#fff' : C.muted,
    textAlign: 'center',
    cursor: 'pointer',
  });

  return (
    <>
      {/* Au magasin comme au bâtiment : les offres et factures, case par case. */}
      {aTraiter}
      {(devis || []).length + (factures || []).length > 0 && (
        <SuiviAffaires
          devis={devis}
          factures={factures}
          chantiers={chantiers}
          onNav={onNav}
          estMag={isMag}
          pieces={facturesCompta}
          maintenant={maintenant}
        />
      )}
      <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
        {choixPeriodes.map(([k, d, l]) => (
          <div
            key={l}
            role="tab"
            aria-selected={periode === k && decalage === d}
            onClick={() => setChoix({ periode: k, decalage: d })}
            style={bouton(periode === k && decalage === d)}
          >
            {l}
          </div>
        ))}
      </div>

      <MonAnnee
        periode={libellePeriode}
        factureTtc={factureTtc}
        ca={ca}
        depenses={depenses}
        depensesTtc={depensesTtc}
        comptableAPayer={comptableAPayer}
        tvaAPayer={tvaProvision}
        tvaServeur={positionTva}
        benefice={benefice}
        tresorerie={tresorerie}
        impots={impots}
        dejaPaye={dejaPayeEtat(frais, (d) => dansPeriode(d, debut, fin, maintenant))}
        independant={independant}
        TX={TX}
        onNav={onNav}
      />

      {caParMois.some((x) => x.v > 0) && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="📈"
            title="Facturé par mois (avec TVA)"
            color={C.green}
            sub={DEVISE + ' · ' + tr('12 derniers mois')}
          />
          <div style={{ padding: '6px 13px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 94 }}>
              {caParMois.map((x, i) => {
                const max = Math.max(...caParMois.map((y) => y.v), 1);
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    {/* Le montant au-dessus de la barre : 9.3k se lit sans toucher. */}
                    {x.v > 0 && (
                      <span style={{ fontSize: 6.5, fontWeight: 800, color: C.green }}>
                        {enMilliers(x.v)}
                      </span>
                    )}
                    <div
                      style={{
                        width: '100%',
                        height: Math.max(2, Math.round((x.v / max) * 64)),
                        background: C.green,
                        borderRadius: '2px 2px 0 0',
                      }}
                      title={DEVISE + ' ' + fmt(Math.round(x.v))}
                    />
                    <span style={{ fontSize: 6.5, color: C.muted }}>{x.lb}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Une seule année : le détail répétait mot pour mot « Mon année »
          juste au-dessus (même facturé, même bénéfice, ligne = total). */}
      {detail && detail.totalCa > 0 && detail.lignes.length > 1 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="📅"
            title={detail.titre}
            color={C.navy}
            sub="Facturé avec et sans TVA, et bénéfice"
          />
          <div style={{ paddingBottom: 4 }}>
            <div
              style={{
                display: 'flex',
                padding: '6px 12px',
                fontSize: 8,
                fontWeight: 800,
                color: C.muted,
                textTransform: 'uppercase',
                borderBottom: '1px solid ' + C.border,
              }}
            >
              <span style={{ flex: 1 }} />
              <span style={COLONNE}>{tr('avec TVA')}</span>
              <span style={COLONNE}>{tr('sans TVA')}</span>
              <span style={COLONNE}>{tr('Bénéfice')}</span>
            </div>
            {detail.lignes.map((l) => {
              // « dont acomptes » : ce que l'année compte pour des factures
              // datées d'une autre année (7'807 touchés en 2025, facture 2026).
              const acomptes = l.annee != null ? acomptesDeLAnnee(l.annee) : [];
              return (
                <div key={l.libelle} style={{ borderTop: '1px solid ' + C.border }}>
                  <div style={{ display: 'flex', padding: '9px 12px', alignItems: 'center' }}>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: l.ca > 0 ? C.ink : C.muted,
                      }}
                    >
                      {l.libelle}
                    </span>
                    {/*
                      Une periode sans recette affiche un tiret plutot qu'un zero :
                      un mois a venir n'est pas un mois a zero, et l'oeil doit les
                      distinguer d'un coup.
                    */}
                    <span style={{ ...CHIFFRE, color: l.ca > 0 ? C.ink : C.muted }}>
                      {l.ca > 0 ? fmt(l.ttc) : '—'}
                    </span>
                    <span style={{ ...CHIFFRE, color: l.ca > 0 ? C.navy : C.muted }}>
                      {l.ca > 0 ? fmt(l.ca) : '—'}
                    </span>
                    <span
                      style={{
                        ...CHIFFRE,
                        color:
                          l.ca > 0 || Math.abs(l.benefice) >= 0.005
                            ? l.benefice >= 0
                              ? C.green
                              : C.red
                            : C.muted,
                      }}
                    >
                      {/* Une année de salaires et de frais sans facture a un
                          bénéfice (négatif) : le tiret le cachait, et le total
                          ne correspondait plus à la somme des lignes. */}
                      {l.ca > 0 || Math.abs(l.benefice) >= 0.005 ? fmt(l.benefice) : '—'}
                    </span>
                  </div>
                  {acomptes.length > 0 && (
                    <div
                      style={{
                        padding: '0 12px 8px',
                        marginTop: -4,
                        fontSize: 9.5,
                        color: C.amber,
                      }}
                    >
                      {trf(
                        'dont acomptes : {0}',
                        acomptes
                          .map((x) => trf('facture {0} : {1}', x.num, fmt(x.montant)))
                          .join(' · '),
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div
              style={{
                display: 'flex',
                padding: '11px 12px',
                borderTop: '2px solid ' + C.border,
                background: C.navyXL,
                alignItems: 'center',
              }}
            >
              <span style={{ flex: 1, fontSize: 12, fontWeight: 900, color: C.ink }}>Total</span>
              <span style={{ ...CHIFFRE, fontWeight: 900, color: C.ink }}>
                {fmt(detail.totalTtc)}
              </span>
              <span style={{ ...CHIFFRE, fontWeight: 900, color: C.navy }}>
                {fmt(detail.totalCa)}
              </span>
              <span
                style={{
                  ...CHIFFRE,
                  fontWeight: 900,
                  color: detail.totalBenefice >= 0 ? C.green : C.red,
                }}
              >
                {fmt(detail.totalBenefice)}
              </span>
            </div>
          </div>
        </Card>
      )}

      {!isMag && rentabilite.length > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="🏗️"
            title="Rentabilité des chantiers"
            color={C.teal}
            sub={tr('Ce que chaque chantier rapporte : facturé moins ses coûts')}
          />
          <div style={{ padding: '6px 13px 12px' }}>
            {rentabilite.map((c, i) => (
              <div
                key={i}
                onClick={() => onNav('chantiers')}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: i < rentabilite.length - 1 ? '1px solid ' + C.border : 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.ink }}>{c.nom}</div>
                  <div style={{ fontSize: 8.5, color: C.muted }}>
                    {tr(c.statut || '—')} ·{' '}
                    {c.facture > 0
                      ? c.sansCout
                        ? tr('aucun coût noté (achats, heures)')
                        : trf('facturé {0} · gagné {1}', fmt(c.facture), fmt(c.ben))
                      : tr('pas encore facturé')}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 900,
                    flexShrink: 0,
                    color:
                      c.marge == null || c.sansCout
                        ? C.muted
                        : c.marge >= 15
                          ? C.green
                          : c.marge >= 0
                            ? C.amber
                            : C.red,
                  }}
                >
                  {c.marge == null || c.sansCout ? '—' : c.marge + '%'}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {salairesBruts > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="💼"
            title="Masse salariale & charges"
            color={C.navy}
            sub={
              'Estimation mensuelle · charges ~' +
              Math.round(chargesSociales * 100) +
              '% (AVS/AC/LAA/LPP)'
            }
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 8,
              padding: '6px 13px 12px',
            }}
          >
            {[
              [salairesBruts, 'Salaires bruts'],
              [charges, 'Charges soc.'],
              [salairesBruts + charges, 'Coût employeur'],
            ].map(([v, l]) => (
              <div key={l} style={caseSalaire}>
                <div style={{ fontSize: 11.5, fontWeight: 900, color: C.ink }}>
                  {DEVISE} {fmt(v)}
                </div>
                <div style={{ fontSize: 8, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                  {l}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Comme la maquette : la carte n'apparaît que s'il y a du stock valorisé. */}
      {valeurStock.achat > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="📦"
            title="Valeur du stock"
            color={C.navy}
            sub={
              // Ce qui attend au dépôt : pas vendu, pas une charge.
              trf(
                '{0} articles · {1} pièces au dépôt',
                marchandiseAuDepot(stock).articles,
                fmtQte(marchandiseAuDepot(stock).pieces),
              ) +
              ' · ' +
              tr("Estimation au prix d'achat")
            }
          />
          {/* Ce que la marchandise a coûté, ce qu'elle rapportera vendue, et
              le gain : « acheté 50'000.–, la revente apportera 70'000.– ». */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 8,
              padding: '6px 13px 8px',
            }}
          >
            <div style={caseSalaire}>
              <div style={{ fontSize: 11.5, fontWeight: 900, color: C.ink }}>
                {DEVISE} {fmt(Math.round(valeurStock.achat))}
              </div>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                Valeur (achat)
              </div>
            </div>
            <div style={caseSalaire}>
              <div style={{ fontSize: 11.5, fontWeight: 900, color: C.navy }}>
                {DEVISE} {fmt(Math.round(valeurStock.vente))}
              </div>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                {tr('Valeur à la revente')}
              </div>
            </div>
            <div style={caseSalaire}>
              <div style={{ fontSize: 11.5, fontWeight: 900, color: C.green }}>
                {DEVISE} {fmt(Math.round(valeurStock.margePotentielle))}
              </div>
              <div style={{ fontSize: 8, color: C.muted, marginTop: 2, fontWeight: 600 }}>
                Marge potentielle
              </div>
            </div>
          </div>
          {sansPrixDeVente > 0 && (
            <div style={{ fontSize: 8.5, color: C.amber, padding: '0 13px 11px', lineHeight: 1.4 }}>
              {trf(
                '{0} article(s) sans prix de vente : la revente ne les compte pas. À compléter dans Stock.',
                sansPrixDeVente,
              )}
            </div>
          )}
        </Card>
      )}

      {/* Une seule carte pour les clients : les meilleurs, puis la fiche et
          « + Nouveau client ». Deux cartes se suivaient et se répétaient. */}
      {topClients.length === 0 && carteClients && (
        <Card style={{ marginBottom: 12 }}>{carteClients}</Card>
      )}
      {topClients.length > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="🏆"
            title="Meilleurs clients"
            color={C.green}
            sub={tr("Classés par chiffre d'affaires facturé") + ' · ' + tr('HT')}
          />
          <div style={{ padding: '6px 13px 12px' }}>
            {topClients.map((c, i) => (
              <div
                key={c.id ?? i}
                onClick={() => onNav('clients')}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '9px 0',
                  borderBottom: i < topClients.length - 1 ? '1px solid ' + C.border : 'none',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span
                    style={{
                      fontSize: 14,
                      width: 22,
                      textAlign: 'center',
                      fontWeight: 800,
                      color: C.muted,
                    }}
                  >
                    {['🥇', '🥈', '🥉'][i] || i + 1 + '.'}
                  </span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.ink }}>{c.nom}</div>
                    <div style={{ fontSize: 8.5, color: C.muted }}>
                      {c.nb} facture{c.nb > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 900, color: C.green }}>
                  {DEVISE} {fmt(c.ca)} {tr('HT')}
                </div>
              </div>
            ))}
          </div>
          {carteClients && <div style={{ borderTop: '1px solid ' + C.border }}>{carteClients}</div>}
        </Card>
      )}
    </>
  );
}
